import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from "react";
import NominalLedgerFilter from "./NominalLedgerFilter";
import ListPageHeader from "../../utilities/ListPageHeader";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.min.css";
import OAuthService from "src/app/auth/services/oAuthService";
import oAuthServiceConfig from "src/app/auth/services/oAuthService/oAuthServiceConfig";
import { styled } from "@mui/material/styles";
import { Box } from "@mui/system";
import {Accordion, AccordionDetails, AccordionSummary, Grid, TextField, Typography} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import moment from "moment";

const useEnhancedEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const processData = (data) => {
    const flattenedData = [];
    const flattenRowRecursive = (row, parentPath) => {
        const dataPath = [...parentPath, row.particulars];
        // const dataType = [...parentType, row.accountType];
        flattenedData.push({ ...row, dataPath });
    };
    data.forEach((row) => flattenRowRecursive(row, []));
    return flattenedData
};


const ColorGrid = styled(Box)(({ theme }) => ({
    "& .rag-head-outer": {
        backgroundColor: "#1F252A",
    },
    "& .rag-group-outer": {
        backgroundColor: "#252B31",
    },
    "& .rag-general-ledger-outer": {
        backgroundColor: "#353C44",
    },
    "& .rag-ledger-outer": {
        backgroundColor: "#3D4853",
    },
    "& .rag-sub-ledger-outer": {
        backgroundColor: "#404D5A",
    },
}));

const NominalLedger = () => {
    const [rowData, setRowData] = useState([]);
    const [filterData, setFilterData] = useState({
        companyCode: "",
        voucherType: "ALL",
        startDate: "",
        endDate: "",
        currency: "EUR",
        accountNo: "",
        accountName: "",
        accountType: "",
        includeOpeningBalance: false
    });
    const [pinnedRowData, setPinnedRowData] = useState([]);
    const [pinnedData,setPinnedData]=useState({
        balance:0,
        totalCredit:0,
        totalDebit:0
    })
    const [selectedRows, setSelectedRows] = useState([]);
    const [showPaginationDropdown, setShowPaginationDropdown] = useState(false);
    const [error, setError] = useState('');

    const url = `${process.env.REACT_APP_FINANCE_API_KEY_URL}${oAuthServiceConfig.generateLedger}`;

    const gridRef = useRef();
    const [gridApi, setGridApi] = useState(null);

    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    const onBtExport = useCallback(() => {
        const params = {
            fileName: "NominalLedger.xlsx",
            sheetName: "Nominal Ledger",
        };
        gridRef.current.api.exportDataAsExcel(params);
    }, []);

    const [columnDefs, setColumnDefs] = useState([
        { field: "sNo.", headerName: "Sno.", minWidth: 50, maxWidth: 50 },
        { field: "docDate", headerName: "Doc Date", minWidth: 160, maxWidth: 160 },
        { field: "voucherType", headerName: "Voucher Type", minWidth: 180, maxWidth: 180 },
        { field: "voucherNo", headerName: "Voucher No.", minWidth: 180, maxWidth: 180 },
        { field: "particulars", headerName: "Particulars", minWidth: 280, maxWidth: 280 },
        { field: "currency", headerName: "Curr.", minWidth: 160, maxWidth: 160 },
        { field: "debit", headerName: "Debit", minWidth: 160, maxWidth: 160 },
        { field: "credit", headerName: "Credit", minWidth: 160, maxWidth: 160 },
        { field: "runningBalance", headerName: "Running Balance", minWidth: 160, maxWidth: 160 },
        { field: "dRCR", headerName: "Dr/Cr", minWidth: 160, maxWidth: 160 }
    ]);

    const autoGroupColumnDef = useMemo(() => {
        return {
            //filter: "agSetColumnFilter",
            cellRendererParams: {
                suppressCount: true,
            },
            minWidth: 0,
            maxWidth: 0,
        };
    }, []);

    const defaultColDef = useMemo(() => {
        return {
            flex: 1,
            floatingFilter: true,
            sortable: true,
            resizable: true,
            filter: 'agMultiColumnFilter',
        };
    }, []);

    const onFirstDataRendered = useCallback((params) => {
        gridRef.current.api.expandAll();
    }, []);

    const getDataPath = useMemo(() => {
        return (data) => data.dataPath;
    }, []);

    const getRowId = useMemo(() => {
        return (params) => params.data.accountNo;
    }, []);

    // const rowClassRules = {
    //     "rag-head-outer": function (params) {
    //         return params?.data?.accountType === "HEAD";
    //     },
    //     "rag-group-outer": function (params) {
    //         return params?.data?.accountType === "GROUP";
    //     },
    //     "rag-general-ledger-outer": function (params) {
    //         return params?.data?.accountType === "GENERAL_LEDGER";
    //     },
    //     "rag-ledger-outer": function (params) {
    //         return params?.data?.accountType === "LEDGER";
    //     },
    //     "rag-sub-ledger-outer": function (params) {
    //         return params?.data?.accountType === "SUB_LEDGER";
    //     },
    // };

    const handleSearchCompany = useCallback((year) => {
        var countryFilterComponent = gridRef.current.api.getFilterInstance("year");
        countryFilterComponent.setModel({ values: [year] });
        gridRef.current.api.onFilterChanged();
    }, []);

    // const handleSearch = useCallback((value) => {
    //     gridRef.current.api.setQuickFilter(value);
    // }, []);

    const handleChange = async (e, checkbox,action) => {
        if (checkbox === "currency") {
            setFilterData(prevValues => ({
                ...prevValues,
                ["currency"]: e.value
            }))
        }
        else if (checkbox === "accountType") {
            if (e.value !== '') {
                setError("")
            }
            setFilterData(prevValues => ({
                ...prevValues,
                ["accountType"]: e.value
            }))
        }
        else if (checkbox === "voucherType") {
            setFilterData(prevValues => ({
                ...prevValues,
                ["voucherType"]: action.action === "clear"?"ALL":e?.id
            }))
        }
        else if (checkbox === "startDate") {
            setFilterData(prevValues => ({
                ...prevValues,
                ["startDate"]: moment(e).format('YYYY-MM-DD'),
            }))
        }
        else if (checkbox === "endDate") {
            setFilterData(prevValues => ({
                ...prevValues,
                ["endDate"]: moment(e).format('YYYY-MM-DD'),
            }))
        }
        else {
            const name = e.target.name;
            const value = e.target.value;
            if (checkbox === "checkbox") {
                const checked = e.target.checked
                if (checked) {
                    setFilterData(prevValues => ({
                        ...prevValues,
                        [name]: true
                    }))
                }
                else {
                    setFilterData(prevValues => ({
                        ...prevValues,
                        [name]: false
                    }))
                }
            }
            else {
                setFilterData(prevValues => ({
                    ...prevValues,
                    [name]: value
                }))
            }
        }
    }

    const handleSearchData = async () => {
        if (filterData.accountType === "") {
            setError("accountType")
        }
        else if (filterData.accountNo === "") {
            setError("accountNo")
        }
        else if (filterData.accountName === "") {
            setError("accountName")
        }
        else if (filterData.startDate === "") {
            setError("startDate");
        }
        else if (filterData.endDate === "") {
            setError("endDate")
        }
        else {
            try {
                const list = await OAuthService.getPostList(url + "/generateLedger", filterData);
                setRowData(processData(list.data.ledgerTransactionDetail));
                const totals = {
                    item: 'total',
                    totalDebit: list.data.totalDebit,
                    totalCredit: list.data.totalCredit,
                    balance: list.data.balance
                }
                setPinnedData((pre)=>({...pre, totalDebit: list.data.totalDebit,
                    totalCredit: list.data.totalCredit,
                    balance: list.data.balance}))
                setPinnedRowData([totals])
            } catch (error) {
                setError(error.message);
            }
        }
    };

    const onBtPrint = useCallback(() => {
        const api = gridRef.current.api;
        setPrinterFriendly(api);
        setTimeout(function () {
            print();
            setNormal(api)
        }, 2000);
    }, [print]);

    const setPrinterFriendly = (api) => {
        const eGridDiv = document.getElementById('chartOfAccountGrid');
        eGridDiv.style.width = '';
        eGridDiv.style.height = '';
        api.setDomLayout('print');
    }

    function setNormal(api) {
        const eGridDiv = document.getElementById('chartOfAccountGrid');
        eGridDiv.style.width = '700px';
        eGridDiv.style.height = '200px';

        api.setDomLayout();
    }

    const onRowDoubleClicked = (params) => {
        handleEditRowDialogOpen(params)
    };

    const onSelectionChanged = (params) => {
        setSelectedRows(params.api.getSelectedRows());
    };

    const [isFullScreen, setIsFullScreen] = useState(false);
    const elementGridRef = useRef(null);

    useEnhancedEffect(() => {
        document.onfullscreenchange = () =>
            setIsFullScreen(document[getBrowserFullscreenElementProp()] != null);

        return () => {
            document.onfullscreenchange = undefined;
        };
    });

    function getBrowserFullscreenElementProp() {
        if (typeof document.fullscreenElement !== 'undefined') {
            return 'fullscreenElement';
        }
        if (typeof document.mozFullScreenElement !== 'undefined') {
            return 'mozFullScreenElement';
        }
        if (typeof document.msFullscreenElement !== 'undefined') {
            return 'msFullscreenElement';
        }
        if (typeof document.webkitFullscreenElement !== 'undefined') {
            return 'webkitFullscreenElement';
        }
        throw new Error('fullscreenElement is not supported by this browser');
    }

    /* View in fullscreen */
    function openFullscreen() {
        // const elem = document.documentElement;
        const elem = elementGridRef;

        if (elem.current.requestFullscreen) {
            elem.current.requestFullscreen();
        } else if (elem.current.mozRequestFullScreen) {
            /* Firefox */
            elem.current.mozRequestFullScreen();
        } else if (elem.current.webkitRequestFullscreen) {
            /* Chrome, Safari and Opera */
            elem.current.webkitRequestFullscreen();
        } else if (elem.current.msRequestFullscreen) {
            /* IE/Edge */
            elem.current.msRequestFullscreen();
        }
    }

    /* Close fullscreen */
    function closeFullscreen() {
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
            /* Firefox */
            document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) {
            /* Chrome, Safari and Opera */
            document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
            /* IE/Edge */
            document.msExitFullscreen();
        }
    }

    function toggleFullScreen() {
        if (
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement
        ) {
            closeFullscreen();
        } else {
            openFullscreen();
        }
    }

    return (
        <>
            <ListPageHeader
                componentName="Nominal Ledger"
                onBtExport={onBtExport}
                pageName="Nominal Ledger"
                gridRef={gridRef}
                showPaginationDropdown={showPaginationDropdown}
                onBtPrint={onBtPrint}
            />
            <div className="px-20 pb-4 pt-16">
                <Accordion defaultExpanded={true}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ justifyContent: 'center' }}
                        className={'justify-center'}
                    >
                        <Typography className="w-full text-center text-xl">Select Fields</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <NominalLedgerFilter
                            setFilterData={setFilterData}
                            handleChange={handleChange}
                            handleSearch={handleSearchData}
                            toggleFullScreen={toggleFullScreen}
                            error={error}
                        />
                    </AccordionDetails>
                </Accordion>
            </div>
            <ColorGrid
                id="chartOfAccountGrid"
                // className="h-full pr-24 pl-24 py-20 ag-theme-alpine-dark grid mt-20"
                className="h-full pr-24 pl-24 ag-theme-alpine-dark grid"
            >
                <AgGridReact
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    ref={gridRef}
                    autoGroupColumnDef={autoGroupColumnDef}
                    onGridReady={onGridReady}
                    rowData={rowData}
                    rowGroupPanelShow={true}
                    // tableClassName={"border border-slate-400"}
                    editable={false}
                    pagination={false}
                    treeData={true}
                    animateRows={true}
                    getDataPath={getDataPath}
                    pinnedBottomRowData={pinnedRowData}
                    // getRowId={getRowId}
                    // rowClassRules={rowClassRules}
                    onSelectionChanged={onSelectionChanged}
                    onRowDoubleClicked={onRowDoubleClicked}
                    showSidebar={true}
                    headerHeight={28}
                    rowHeight={22}
                    onFirstDataRendered={onFirstDataRendered}
                // rowGroupPanelShow={'hide'}
                />

            </ColorGrid>
            <Box className="m-5" >
                <Grid container spacing={2} className="justify-center mt-1" >
                    <Grid item xs={2}>
                        <label className="basis-0 float-left">Total Debit :</label>
                        <TextField
                            className="w-[100%]"
                            name='totalDebits'
                            value={pinnedData?.totalDebit}
                            size="small"
                            InputProps={{
                                readOnly: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <label className="basis-0 float-left">Total Credit :</label>
                        <TextField
                            className="w-[100%]"
                            name='totalDebits'
                            value={pinnedData?.totalCredit}
                            size="small"
                            InputProps={{
                                readOnly: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <label className="basis-0 float-left">Balance :</label>
                        <TextField
                            className="w-[100%]"
                            name='totalDebits'
                            value={pinnedData?.balance}
                            size="small"
                            InputProps={{
                                readOnly: true
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default NominalLedger;
